import React, { useState, useEffect } from 'react';
import { X, Download, Package, Server, Code, Terminal, CheckCircle, AlertCircle, Loader, FileDown, Cpu, Globe } from 'lucide-react';
import { Agent } from '../lib/supabase';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { useAuth } from '../lib/AuthContext';

interface AgentDownloadModalProps {
  agent: Agent;
  isOpen: boolean;
  onClose: () => void;
}

type DownloadFormat = 'zip' | 'executable' | 'docker';
type Platform = 'windows' | 'mac' | 'linux' | 'all';

export function AgentDownloadModal({ agent, isOpen, onClose }: AgentDownloadModalProps) {
  const [downloadFormat, setDownloadFormat] = useState<DownloadFormat>('zip');
  const [platform, setPlatform] = useState<Platform>('all');
  const [includeConfig, setIncludeConfig] = useState(true);
  const [includeReadme, setIncludeReadme] = useState(true);
  const [includeDependencies, setIncludeDependencies] = useState(true);
  const [step, setStep] = useState<'options' | 'preparing' | 'downloading' | 'complete' | 'error'>('options');
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const { user } = useAuth();

  useEffect(() => {
    if (isOpen) {
      setStep('options');
      setError(null);
      setProgress(0);
    }
  }, [isOpen]);

  const handleDownload = async () => {
    if (!agent) return;
    
    setStep('preparing');
    setProgress(10);
    
    try {
      // Simulate API call to get agent code and dependencies
      await new Promise(resolve => setTimeout(resolve, 1000));
      setProgress(30);
      
      if (downloadFormat === 'zip') {
        await generateZipPackage();
      } else if (downloadFormat === 'executable') {
        await generateExecutable();
      } else if (downloadFormat === 'docker') {
        await generateDockerPackage();
      }
      
      // Track download in analytics
      try {
        await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/analytics-tracker?action=track`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            event_type: 'agent_download',
            user_id: user?.id,
            session_id: 'download_session',
            agent_id: agent.id,
            metadata: {
              format: downloadFormat,
              platform,
              includeConfig,
              includeReadme,
              includeDependencies
            }
          })
        });
      } catch (err) {
        console.error('Failed to track download:', err);
        // Don't fail the download if tracking fails
      }
      
      setStep('complete');
    } catch (err: any) {
      console.error('Download error:', err);
      setError(err.message || 'Failed to download agent');
      setStep('error');
    }
  };

  const generateZipPackage = async () => {
    setProgress(40);
    
    const zip = new JSZip();
    
    // Add agent code files
    const mainCode = await generateAgentCode(agent);
    zip.file(`${agent.name.toLowerCase().replace(/\s+/g, '-')}/index.js`, mainCode);
    
    // Add package.json
    const packageJson = generatePackageJson(agent);
    zip.file(`${agent.name.toLowerCase().replace(/\s+/g, '-')}/package.json`, packageJson);
    
    if (includeConfig) {
      // Add configuration file
      const configFile = generateConfigFile(agent);
      zip.file(`${agent.name.toLowerCase().replace(/\s+/g, '-')}/config.json`, configFile);
    }
    
    if (includeReadme) {
      // Add README
      const readme = generateReadme(agent);
      zip.file(`${agent.name.toLowerCase().replace(/\s+/g, '-')}/README.md`, readme);
    }
    
    // Add setup scripts
    const setupScript = generateSetupScript(agent, platform);
    if (platform === 'windows' || platform === 'all') {
      zip.file(`${agent.name.toLowerCase().replace(/\s+/g, '-')}/setup.bat`, setupScript.windows);
    }
    if (platform === 'mac' || platform === 'linux' || platform === 'all') {
      zip.file(`${agent.name.toLowerCase().replace(/\s+/g, '-')}/setup.sh`, setupScript.unix);
    }
    
    setProgress(70);
    
    // Generate the zip file
    const content = await zip.generateAsync({ type: 'blob' });
    
    setProgress(90);
    setStep('downloading');
    
    // Save the zip file
    saveAs(content, `${agent.name.toLowerCase().replace(/\s+/g, '-')}-agent.zip`);
    
    setProgress(100);
  };

  const generateExecutable = async () => {
    setProgress(40);
    
    // For demo purposes, we'll just create a zip with a README explaining this is a demo
    const zip = new JSZip();
    
    const executableReadme = `# ${agent.name} Executable Package

This is a demonstration of the executable package feature. In a production environment, this would be a compiled executable for ${platform === 'all' ? 'cross-platform' : platform} that includes:

1. The agent code and dependencies
2. A runtime environment
3. Configuration files
4. A user interface for interacting with the agent

## How to use this demo

Since this is a demonstration, we've included the agent code as JavaScript files that you can run with Node.js:

\`\`\`
npm install
npm start
\`\`\`

In a real implementation, you would simply run the executable file.

## Agent Information

Name: ${agent.name}
Description: ${agent.description}
Source: ${agent.source}
Author: ${agent.author}
`;
    
    zip.file(`${agent.name.toLowerCase().replace(/\s+/g, '-')}/README.md`, executableReadme);
    
    // Add the same files as the zip package
    const mainCode = await generateAgentCode(agent);
    zip.file(`${agent.name.toLowerCase().replace(/\s+/g, '-')}/index.js`, mainCode);
    
    const packageJson = generatePackageJson(agent);
    zip.file(`${agent.name.toLowerCase().replace(/\s+/g, '-')}/package.json`, packageJson);
    
    if (includeConfig) {
      const configFile = generateConfigFile(agent);
      zip.file(`${agent.name.toLowerCase().replace(/\s+/g, '-')}/config.json`, configFile);
    }
    
    setProgress(70);
    
    // Generate the zip file
    const content = await zip.generateAsync({ type: 'blob' });
    
    setProgress(90);
    setStep('downloading');
    
    // Save the zip file
    saveAs(content, `${agent.name.toLowerCase().replace(/\s+/g, '-')}-executable.zip`);
    
    setProgress(100);
  };

  const generateDockerPackage = async () => {
    setProgress(40);
    
    // For demo purposes, we'll create a zip with Docker files
    const zip = new JSZip();
    
    // Add Dockerfile
    const dockerfile = generateDockerfile(agent);
    zip.file(`${agent.name.toLowerCase().replace(/\s+/g, '-')}/Dockerfile`, dockerfile);
    
    // Add docker-compose.yml
    const dockerCompose = generateDockerCompose(agent);
    zip.file(`${agent.name.toLowerCase().replace(/\s+/g, '-')}/docker-compose.yml`, dockerCompose);
    
    // Add agent code
    const mainCode = await generateAgentCode(agent);
    zip.file(`${agent.name.toLowerCase().replace(/\s+/g, '-')}/index.js`, mainCode);
    
    // Add package.json
    const packageJson = generatePackageJson(agent);
    zip.file(`${agent.name.toLowerCase().replace(/\s+/g, '-')}/package.json`, packageJson);
    
    if (includeConfig) {
      // Add configuration file
      const configFile = generateConfigFile(agent);
      zip.file(`${agent.name.toLowerCase().replace(/\s+/g, '-')}/config.json`, configFile);
    }
    
    if (includeReadme) {
      // Add README with Docker instructions
      const dockerReadme = generateDockerReadme(agent);
      zip.file(`${agent.name.toLowerCase().replace(/\s+/g, '-')}/README.md`, dockerReadme);
    }
    
    setProgress(70);
    
    // Generate the zip file
    const content = await zip.generateAsync({ type: 'blob' });
    
    setProgress(90);
    setStep('downloading');
    
    // Save the zip file
    saveAs(content, `${agent.name.toLowerCase().replace(/\s+/g, '-')}-docker.zip`);
    
    setProgress(100);
  };

  const generateAgentCode = async (agent: Agent): Promise<string> => {
    // In a real implementation, this would fetch the actual agent code
    // For demo purposes, we'll generate a simple agent based on the metadata
    
    return `// ${agent.name} - AI Agent
// Author: ${agent.author}
// Source: ${agent.source}
// Generated by Agent Store

const config = require('./config.json');

class ${agent.name.replace(/\s+/g, '')}Agent {
  constructor() {
    this.name = "${agent.name}";
    this.description = "${agent.description}";
    this.config = config;
    console.log(\`Initializing \${this.name}...\`);
  }

  async initialize() {
    console.log("Agent initialized successfully!");
    return true;
  }

  async process(input) {
    console.log(\`Processing input: \${input}\`);
    
    // This is where the agent's actual logic would go
    // For demo purposes, we'll just return a simple response
    return {
      input,
      timestamp: new Date().toISOString(),
      response: \`\${this.name} processed: \${input}\`,
      agent: this.name
    };
  }

  async shutdown() {
    console.log("Agent shutting down...");
    return true;
  }
}

// Export the agent
module.exports = ${agent.name.replace(/\s+/g, '')}Agent;

// If running directly, start a simple CLI
if (require.main === module) {
  const readline = require('readline');
  const agent = new ${agent.name.replace(/\s+/g, '')}Agent();
  
  const rl = readline.createInterface({
    input: process.stdin,
    output: process.stdout
  });

  agent.initialize().then(() => {
    console.log(\`\${agent.name} is ready. Type your input (or 'exit' to quit):\`);
    
    rl.on('line', async (input) => {
      if (input.toLowerCase() === 'exit') {
        await agent.shutdown();
        rl.close();
        return;
      }
      
      const result = await agent.process(input);
      console.log("Response:", result.response);
      console.log("\\nType your next input (or 'exit' to quit):");
    });
  });
}
`;
  };

  const generatePackageJson = (agent: Agent): string => {
    return JSON.stringify({
      name: agent.name.toLowerCase().replace(/\s+/g, '-'),
      version: "1.0.0",
      description: agent.description,
      main: "index.js",
      scripts: {
        start: "node index.js",
        test: "echo \"Error: no test specified\" && exit 1"
      },
      keywords: agent.tags || [],
      author: agent.author,
      license: "MIT",
      dependencies: {
        // Add basic dependencies based on agent type
        ...(agent.language?.toLowerCase().includes('python') ? {} : {
          "dotenv": "^16.0.3"
        })
      }
    }, null, 2);
  };

  const generateConfigFile = (agent: Agent): string => {
    return JSON.stringify({
      name: agent.name,
      description: agent.description,
      version: "1.0.0",
      settings: {
        logLevel: "info",
        maxConcurrentRequests: 10,
        timeout: 30000
      },
      api: {
        enabled: false,
        port: 3000,
        host: "localhost"
      },
      // Add placeholder for API keys
      apiKeys: {
        openai: "YOUR_OPENAI_API_KEY",
        google: "YOUR_GOOGLE_API_KEY"
      }
    }, null, 2);
  };

  const generateReadme = (agent: Agent): string => {
    return `# ${agent.name}

${agent.description}

## About

- **Author:** ${agent.author}
- **Source:** ${agent.source}
- **Language:** ${agent.language || 'JavaScript'}
- **Framework:** ${agent.framework || 'N/A'}

## Installation

1. Ensure you have [Node.js](https://nodejs.org/) installed (version 14 or higher)
2. Navigate to the agent directory
3. Run \`npm install\` to install dependencies
4. Configure the agent by editing \`config.json\`
5. Run \`npm start\` to start the agent

## Configuration

Edit the \`config.json\` file to customize the agent's behavior:

\`\`\`json
{
  "name": "${agent.name}",
  "settings": {
    "logLevel": "info",
    "maxConcurrentRequests": 10,
    "timeout": 30000
  },
  "apiKeys": {
    "openai": "YOUR_OPENAI_API_KEY",
    "google": "YOUR_GOOGLE_API_KEY"
  }
}
\`\`\`

## Usage

Once started, the agent provides a simple command-line interface:

\`\`\`
$ npm start
${agent.name} is ready. Type your input (or 'exit' to quit):
> Hello
Response: ${agent.name} processed: Hello

Type your next input (or 'exit' to quit):
> exit
Agent shutting down...
\`\`\`

## Advanced Usage

For programmatic usage, you can import the agent in your own code:

\`\`\`javascript
const ${agent.name.replace(/\s+/g, '')}Agent = require('./index.js');

async function main() {
  const agent = new ${agent.name.replace(/\s+/g, '')}Agent();
  await agent.initialize();
  
  const result = await agent.process("Your input here");
  console.log(result.response);
  
  await agent.shutdown();
}

main().catch(console.error);
\`\`\`

## License

MIT
`;
  };

  const generateSetupScript = (agent: Agent, platform: Platform): { windows: string, unix: string } => {
    const windows = `@echo off
echo Installing ${agent.name}...
echo.

:: Check if Node.js is installed
where node >nul 2>nul
if %ERRORLEVEL% neq 0 (
  echo Node.js is not installed. Please install Node.js from https://nodejs.org/
  exit /b 1
)

:: Install dependencies
echo Installing dependencies...
call npm install
if %ERRORLEVEL% neq 0 (
  echo Failed to install dependencies.
  exit /b 1
)

echo.
echo ${agent.name} has been successfully installed!
echo To start the agent, run: npm start
echo.

pause
`;

    const unix = `#!/bin/bash
echo "Installing ${agent.name}..."
echo

# Check if Node.js is installed
if ! command -v node &> /dev/null; then
  echo "Node.js is not installed. Please install Node.js from https://nodejs.org/"
  exit 1
fi

# Make script executable
chmod +x index.js

# Install dependencies
echo "Installing dependencies..."
npm install
if [ $? -ne 0 ]; then
  echo "Failed to install dependencies."
  exit 1
fi

echo
echo "${agent.name} has been successfully installed!"
echo "To start the agent, run: npm start"
echo
`;

    return { windows, unix };
  };

  const generateDockerfile = (agent: Agent): string => {
    return `# Dockerfile for ${agent.name}
FROM node:18-alpine

WORKDIR /app

# Copy package.json and package-lock.json
COPY package*.json ./

# Install dependencies
RUN npm ci --only=production

# Copy application code
COPY . .

# Expose port if API is enabled
EXPOSE 3000

# Run the agent
CMD ["node", "index.js"]
`;
  };

  const generateDockerCompose = (agent: Agent): string => {
    return `version: '3.8'

services:
  ${agent.name.toLowerCase().replace(/\s+/g, '-')}:
    build: .
    container_name: ${agent.name.toLowerCase().replace(/\s+/g, '-')}
    volumes:
      - ./config.json:/app/config.json
    ports:
      - "3000:3000"
    environment:
      - NODE_ENV=production
    restart: unless-stopped
`;
  };

  const generateDockerReadme = (agent: Agent): string => {
    return `# ${agent.name} - Docker Edition

${agent.description}

## Docker Installation

### Prerequisites

- [Docker](https://www.docker.com/get-started) installed on your system
- [Docker Compose](https://docs.docker.com/compose/install/) (optional but recommended)

### Quick Start

1. Navigate to the agent directory
2. Build and start the container:

   \`\`\`bash
   docker-compose up -d
   \`\`\`

3. To view logs:

   \`\`\`bash
   docker-compose logs -f
   \`\`\`

4. To stop the container:

   \`\`\`bash
   docker-compose down
   \`\`\`

### Manual Docker Build

If you prefer not to use Docker Compose:

1. Build the Docker image:

   \`\`\`bash
   docker build -t ${agent.name.toLowerCase().replace(/\s+/g, '-')} .
   \`\`\`

2. Run the container:

   \`\`\`bash
   docker run -d --name ${agent.name.toLowerCase().replace(/\s+/g, '-')} -p 3000:3000 ${agent.name.toLowerCase().replace(/\s+/g, '-')}
   \`\`\`

## Configuration

The agent uses the \`config.json\` file for configuration. You can modify this file and restart the container to apply changes.

## About

- **Author:** ${agent.author}
- **Source:** ${agent.source}
- **Language:** ${agent.language || 'JavaScript'}
- **Framework:** ${agent.framework || 'N/A'}

## License

MIT
`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Download className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Download Agent</h2>
              <p className="text-sm text-gray-600">{agent.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 'options' && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Download Options
                </h3>
                <p className="text-blue-700 text-sm">
                  Configure how you want to download and run this agent locally on your machine.
                </p>
              </div>

              {/* Download Format */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Download Format</label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <label className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                    downloadFormat === 'zip' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                  }`}>
                    <input
                      type="radio"
                      name="downloadFormat"
                      value="zip"
                      checked={downloadFormat === 'zip'}
                      onChange={() => setDownloadFormat('zip')}
                      className="text-blue-600"
                    />
                    <div className="flex items-center gap-2">
                      <FileDown className="w-5 h-5 text-blue-600" />
                      <div>
                        <div className="font-medium">Source Code (ZIP)</div>
                        <div className="text-xs text-gray-500">Raw code files with setup scripts</div>
                      </div>
                    </div>
                  </label>

                  <label className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                    downloadFormat === 'executable' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                  }`}>
                    <input
                      type="radio"
                      name="downloadFormat"
                      value="executable"
                      checked={downloadFormat === 'executable'}
                      onChange={() => setDownloadFormat('executable')}
                      className="text-blue-600"
                    />
                    <div className="flex items-center gap-2">
                      <Cpu className="w-5 h-5 text-green-600" />
                      <div>
                        <div className="font-medium">Executable</div>
                        <div className="text-xs text-gray-500">Standalone application</div>
                      </div>
                    </div>
                  </label>

                  <label className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                    downloadFormat === 'docker' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                  }`}>
                    <input
                      type="radio"
                      name="downloadFormat"
                      value="docker"
                      checked={downloadFormat === 'docker'}
                      onChange={() => setDownloadFormat('docker')}
                      className="text-blue-600"
                    />
                    <div className="flex items-center gap-2">
                      <Server className="w-5 h-5 text-purple-600" />
                      <div>
                        <div className="font-medium">Docker Container</div>
                        <div className="text-xs text-gray-500">Containerized environment</div>
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Platform Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Platform</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <label className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    platform === 'all' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                  }`}>
                    <input
                      type="radio"
                      name="platform"
                      value="all"
                      checked={platform === 'all'}
                      onChange={() => setPlatform('all')}
                      className="text-blue-600"
                    />
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-blue-600" />
                      <span>All Platforms</span>
                    </div>
                  </label>

                  <label className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    platform === 'windows' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                  }`}>
                    <input
                      type="radio"
                      name="platform"
                      value="windows"
                      checked={platform === 'windows'}
                      onChange={() => setPlatform('windows')}
                      className="text-blue-600"
                    />
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-blue-600" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 5.1L10.5 4.5V11H3V5.1Z" fill="currentColor"/>
                        <path d="M3 13H10.5V19.5L3 18.9V13Z" fill="currentColor"/>
                        <path d="M12.5 4.2L21 3.5V11H12.5V4.2Z" fill="currentColor"/>
                        <path d="M12.5 13H21V20.5L12.5 19.8V13Z" fill="currentColor"/>
                      </svg>
                      <span>Windows</span>
                    </div>
                  </label>

                  <label className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    platform === 'mac' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                  }`}>
                    <input
                      type="radio"
                      name="platform"
                      value="mac"
                      checked={platform === 'mac'}
                      onChange={() => setPlatform('mac')}
                      className="text-blue-600"
                    />
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-gray-800" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09997 22C7.78997 22.05 6.79997 20.68 5.95997 19.47C4.24997 17 2.93997 12.45 4.69997 9.39C5.56997 7.87 7.12997 6.91 8.81997 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.09 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z" fill="currentColor"/>
                      </svg>
                      <span>macOS</span>
                    </div>
                  </label>

                  <label className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    platform === 'linux' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                  }`}>
                    <input
                      type="radio"
                      name="platform"
                      value="linux"
                      checked={platform === 'linux'}
                      onChange={() => setPlatform('linux')}
                      className="text-blue-600"
                    />
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-gray-800" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2C11.05 2 10.1 2.2 9.3 2.6C8.5 3 7.8 3.5 7.3 4.2C6.8 4.9 6.5 5.6 6.3 6.5C6.1 7.4 6.1 8.2 6.3 9.1C6.1 9.2 5.9 9.3 5.8 9.5C5.6 9.7 5.5 9.9 5.4 10.2C5.3 10.5 5.2 10.7 5.2 11C5.2 11.3 5.2 11.7 5.2 12C5.2 12.3 5.2 12.7 5.2 13C5.2 13.3 5.3 13.7 5.4 14C5.5 14.3 5.7 14.6 5.9 14.8C6.1 15 6.4 15.2 6.7 15.2C6.8 15.2 6.9 15.2 7 15.2C7 15.3 7 15.4 7 15.5C7 15.6 7 15.8 7 15.9C7 16 7 16.2 7 16.3C7 16.4 7.1 16.6 7.1 16.7C7.1 16.8 7.2 17 7.3 17.1C7.4 17.2 7.4 17.4 7.5 17.5C7.6 17.6 7.7 17.7 7.8 17.8C7.9 17.9 8 18 8.2 18C8.3 18 8.5 18 8.6 18C8.7 18 8.9 17.9 9 17.8C9.1 17.7 9.2 17.7 9.3 17.6C9.4 17.5 9.4 17.4 9.5 17.3C9.6 17.2 9.6 17.1 9.6 17C9.6 16.9 9.7 16.8 9.7 16.7C9.7 16.6 9.7 16.4 9.7 16.3C9.7 16.2 9.7 16 9.7 15.9C9.7 15.8 9.6 15.6 9.6 15.5C9.6 15.4 9.5 15.3 9.5 15.2C9.5 15.1 9.4 15 9.3 14.9C9.2 14.8 9.1 14.8 9 14.7C8.9 14.6 8.8 14.6 8.7 14.6C8.6 14.6 8.4 14.6 8.3 14.6C8.2 14.6 8 14.7 7.9 14.7" fill="currentColor"/>
                      </svg>
                      <span>Linux</span>
                    </div>
                  </label>
                </div>
              </div>

              {/* Include Options */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Include Options</label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={includeConfig}
                      onChange={(e) => setIncludeConfig(e.target.checked)}
                      className="rounded text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Configuration file (config.json)</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={includeReadme}
                      onChange={(e) => setIncludeReadme(e.target.checked)}
                      className="rounded text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Documentation (README.md)</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={includeDependencies}
                      onChange={(e) => setIncludeDependencies(e.target.checked)}
                      className="rounded text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Dependencies and setup scripts</span>
                  </label>
                </div>
              </div>

              {/* Requirements */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h3 className="font-medium text-yellow-900 mb-2 flex items-center gap-2">
                  <Terminal className="w-5 h-5" />
                  Requirements
                </h3>
                <ul className="text-sm text-yellow-800 space-y-1">
                  <li>• Node.js 14+ for running JavaScript agents</li>
                  <li>• Python 3.8+ for Python agents</li>
                  <li>• Docker for containerized deployment</li>
                  <li>• API keys for services used by the agent</li>
                </ul>
              </div>

              <button
                onClick={handleDownload}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-colors"
              >
                <Download className="w-5 h-5" />
                Download Agent
              </button>
            </div>
          )}

          {step === 'preparing' && (
            <div className="text-center py-12">
              <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Preparing Download</h3>
              <p className="text-gray-600 mb-6">Packaging agent files for {platform === 'all' ? 'all platforms' : platform}...</p>
              
              <div className="max-w-md mx-auto bg-gray-100 rounded-full h-4 mb-2">
                <div 
                  className="bg-blue-600 h-4 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-500">{progress}% complete</p>
            </div>
          )}

          {step === 'error' && (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Download Failed</h3>
              <p className="text-red-600 mb-6">{error}</p>
              <button
                onClick={() => setStep('options')}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          )}

          {step === 'complete' && (
            <div className="text-center py-12">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Download Complete</h3>
              <p className="text-gray-600 mb-6">
                Your agent has been downloaded successfully. Follow the instructions in the README file to set up and run the agent.
              </p>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md mx-auto text-left mb-6">
                <h4 className="font-medium text-blue-900 mb-2">Next Steps:</h4>
                <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                  <li>Extract the downloaded zip file</li>
                  <li>Navigate to the extracted directory</li>
                  <li>Run the setup script for your platform</li>
                  <li>Start the agent using the instructions in the README</li>
                </ol>
              </div>
              
              <div className="flex gap-4 justify-center">
                <button
                  onClick={() => setStep('options')}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Download Another Format
                </button>
                <button
                  onClick={onClose}
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}